import React, { FC, useEffect, useRef, useState } from 'react'
import * as Styled from './styled'
import { useStore } from 'effector-react'
import { QRCodeCanvas } from 'qrcode.react'
import {
  $getPaymentWallet,
  $topUpSelectedCurrency,
  setTopUpCurrency,
  getPaymentWalletFx,
  $getPaymentWalletNumber,
  getAvailableMethods,
  $paymentMethods,
  $selectedPaymentMethod,
  setPaymentMethod,
  setTopUpAmount,
  $topUpAmount,
  paymentConfirm,
  resetPaymentMethod,
  $paymentSystem,
  $isPaymentConfirmPending,
} from '../../../model'
import { Button, ButtonType, ButtonVariant } from '@/5_shared/ui'
import * as LanguageStyled from '@/3_features/language-selection/ui/styled'
import { CurrencyCard } from '@/4_entities/currency/ui'
import { UserService } from '@/7_services/user'
import { convertSecondsToTime } from '../../../utils'
import { useTranslation } from 'react-i18next'
import { Currency } from '@/4_entities/currency'
import { banks } from '@/5_shared/constants'
import { useOutsideClick } from '@/5_shared/hooks'

const handleResetCurrency = () => {
  setTopUpCurrency(null)
  resetPaymentMethod()
  setTopUpAmount(0)
}

export const CurrencyStep: FC = () => {
  const topUpSelectedCurrency = useStore($topUpSelectedCurrency)
  const paymentWallet = useStore($getPaymentWallet)
  const walletNumber = useStore($getPaymentWalletNumber)
  const paymentSystem = useStore($paymentSystem)
  const paymentMethods = useStore($paymentMethods)
  const selectedPaymentMethod = useStore($selectedPaymentMethod)
  const topUpAmount = useStore($topUpAmount)
  const isPaymentConfirmPending = useStore($isPaymentConfirmPending)
  const { user } = useStore(UserService.getStore())
  const [time, setTime] = useState('')
  const [seconds, setSeconds] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState('')
  const paymentMethodsRef = useRef<HTMLDivElement>(null)
  const intervalRef = useRef(null)

  useOutsideClick(paymentMethodsRef, isOpen, () => setIsOpen(false))

  const { t } = useTranslation()

  useEffect(() => {
    if (paymentSystem && topUpSelectedCurrency !== 112) {
      getPaymentWalletFx({
        user_id: user?.user_id,
        payment_type: paymentSystem?.id,
      })
    }
    if (paymentSystem) {
      setTopUpAmount(Number(paymentSystem.MinInputSum))
    }
  }, [])
  useEffect(() => {
    if (paymentWallet) {
      setSeconds(paymentWallet.timeout)
    }
  }, [paymentWallet])

  useEffect(() => {
    if (paymentWallet && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        const timeObj = convertSecondsToTime(seconds)

        if (seconds <= 0) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        setTime(`${timeObj.hours}:${timeObj.minutes}:${timeObj.seconds}`)
        setSeconds((prev) => prev - 1)
      }, 1000)
    }
    return () => {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [paymentWallet, seconds])

  if (topUpSelectedCurrency === null) {
    return null
  }
  return (
    <>
      <Styled.CurrencyMainWrap>
        <Styled.CurrencyStepHeadBlock>
          <Styled.CurrencyStepSelectCurrencyWrapper>
            <Styled.SelectCurrencyTitle>
              {t('topUpTab.currency')}
            </Styled.SelectCurrencyTitle>
            {topUpSelectedCurrency === 112 ? (
              <Styled.SelectCurrencyText>
                {t('topUpTab.currencyBankText')}
              </Styled.SelectCurrencyText>
            ) : (
              <Styled.SelectCurrencyText>
                {t('topUpTab.currencyText')}
              </Styled.SelectCurrencyText>
            )}
          </Styled.CurrencyStepSelectCurrencyWrapper>

          <Styled.CurrencyTabCurrencyCardWrapper>
            <CurrencyCard
              currency={user?.currency_list.find(
                (cur: Currency) => cur.CurrencyID === topUpSelectedCurrency
              )}
            />
          </Styled.CurrencyTabCurrencyCardWrapper>
        </Styled.CurrencyStepHeadBlock>
        {topUpSelectedCurrency === 112 ? (
          paymentWallet ? (
            <>
              <Styled.LabeledWithCopy label={t('topUpTab.fio')}>
                <Styled.Input value={paymentWallet.fio ?? ''} disabled />
              </Styled.LabeledWithCopy>
              <Styled.LabeledWithCopy label={t('topUpTab.sum')}>
                <Styled.Input value={paymentWallet.sum ?? ''} disabled />
              </Styled.LabeledWithCopy>
              <Styled.LabeledWithCopy label={t('topUpTab.bank')}>
                <Styled.Input
                  value={banks[paymentWallet.bank] ?? ''}
                  disabled
                />
              </Styled.LabeledWithCopy>
              <Styled.CheckboxBlock>
                <Styled.Checkbox
                  checked={Boolean(paymentWallet.sbp)}
                  id="sbp"
                  disabled
                />
                <Styled.CheckboxLabel htmlFor="sbp">
                  {t('topUpTab.sbp')}
                </Styled.CheckboxLabel>
              </Styled.CheckboxBlock>
              <Styled.Labeled label={t('topUpTab.bankCard')}>
                <Styled.CardInfo>
                  <Styled.LabeledWithCopy label={t('topUpTab.card.number')}>
                    <Styled.Input
                      value={paymentWallet?.card?.number ?? ''}
                      disabled
                    />
                  </Styled.LabeledWithCopy>
                  <Styled.LabeledWithCopy label={t('topUpTab.card.telefon')}>
                    <Styled.Input
                      value={paymentWallet?.card?.telefon ?? ''}
                      disabled
                    />
                  </Styled.LabeledWithCopy>
                  <Styled.LabeledWithCopy
                    label={t('topUpTab.card.bank_id_code')}
                  >
                    <Styled.Input
                      value={paymentWallet?.card?.bank_id_code ?? ''}
                      disabled
                    />
                  </Styled.LabeledWithCopy>
                </Styled.CardInfo>
              </Styled.Labeled>
              <Styled.TextBlock>
                {t('topUpTab.timerLabel')}: {time}
              </Styled.TextBlock>
              <Button
                type={ButtonType.Neutral}
                variant={ButtonVariant.Inverted}
                fluid
                onClick={() => {
                  paymentConfirm({
                    amount: Number(topUpAmount),
                    order_number: paymentWallet?.order_number ?? '',
                    payment_type: paymentSystem?.id ?? 0,
                  })
                }}
                isPending={isPaymentConfirmPending}
                disabled={seconds <= 0}
              >
                {t('topUpTab.confirm')}
              </Button>
            </>
          ) : (
            <>
              <Styled.Labeled
                label={`${t(
                  'topUpTab.amount'
                )} (min ${paymentSystem?.MinInputSum} - max ${paymentSystem?.MaxInputSum})`}
                onRightButtonClick={() => {
                  if (
                    topUpAmount >= Number(paymentSystem?.MinInputSum) &&
                    topUpAmount <= Number(paymentSystem?.MaxInputSum)
                  ) {
                    getAvailableMethods({
                      amount: Number(topUpAmount) * 100,
                      payment_type: paymentSystem?.id ?? 0,
                      currency: topUpSelectedCurrency ?? 0,
                    })
                  }
                }}
                rightContent={t('topUpTab.next')}
              >
                <Styled.Input
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(Number(e.target.value))}
                  type="number"
                  step="1"
                  placeholder="5000"
                  min={paymentSystem?.MinInputSum}
                  max={paymentSystem?.MaxInputSum}
                  disabled={paymentMethods.length > 0}
                />
              </Styled.Labeled>
              {paymentMethods.length > 0 ? (
                <Styled.Select ref={paymentMethodsRef}>
                  <Styled.Labeled
                    label={t('topUpTab.paymentSystem')}
                    onRightButtonClick={() => {
                      if (selectedMethod) {
                        setPaymentMethod(selectedMethod)
                      }
                    }}
                    rightContent={t('topUpTab.next')}
                  >
                    <Styled.SelectedSystem
                      onClick={() => setIsOpen((prevValue) => !prevValue)}
                    >
                      <LanguageStyled.IconWrapper
                        onClick={() => setIsOpen(false)}
                      >
                        {isOpen ? (
                          <LanguageStyled.ChevronUpIcon />
                        ) : (
                          <LanguageStyled.ChevronDownIcon />
                        )}
                      </LanguageStyled.IconWrapper>
                      {banks[selectedMethod] ?? selectedMethod}
                    </Styled.SelectedSystem>
                  </Styled.Labeled>
                  <Styled.Dropdown $isShown={isOpen}>
                    {paymentMethods.map((method) => (
                      <Styled.Option
                        key={method}
                        onClick={() => {
                          setIsOpen(false)
                          setSelectedMethod(method)
                        }}
                        $isSelected={method === selectedPaymentMethod}
                      >
                        {banks[method] ?? method}
                      </Styled.Option>
                    ))}
                  </Styled.Dropdown>
                </Styled.Select>
              ) : null}
            </>
          )
        ) : (
          <>
            <Styled.LabeledWithCopy label={t('topUpTab.walletNumber')}>
              <Styled.Input value={walletNumber} disabled />
            </Styled.LabeledWithCopy>
            <Styled.QrcodeWrapper>
              <QRCodeCanvas
                value={walletNumber}
                width={2}
                height={2}
                size={256}
              ></QRCodeCanvas>
            </Styled.QrcodeWrapper>

            <Styled.TextBlock>
              {t('topUpTab.timerLabel')}: {time}
            </Styled.TextBlock>
          </>
        )}
      </Styled.CurrencyMainWrap>

      <Button
        type={ButtonType.Neutral}
        variant={ButtonVariant.Inverted}
        fluid
        onClick={handleResetCurrency}
      >
        {t('buttons.cancel')}
      </Button>
    </>
  )
}

CurrencyStep.displayName = 'CurrencyStep'
